import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";

const Indent = Extension.create({
  name: "sermonIndent",
  addGlobalAttributes() { return [{ types: ["paragraph", "heading"], attributes: {
    indent: { default: 0, parseHTML: el => Math.min(4, Math.floor(parseInt(el.style.marginLeft || "0", 10) / 32) || 0),
      renderHTML: attrs => attrs.indent ? { style: `margin-left: ${attrs.indent * 32}px` } : {} }
  }}]; },
});

const button = "px-2 py-1 rounded border text-sm min-w-8 hover:bg-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]";

export default function SermonOutlineEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: false, underline: false }), Underline, Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }), Link.configure({ openOnClick: false, autolink: false }),
      TextStyle, Color, FontSize, Indent],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "prose max-w-none min-h-[260px] p-4 focus:outline-none", "aria-label": "Sermon outline text" } },
  });
  useEffect(() => { if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false }); }, [value, editor]);
  if (!editor) return null;
  const control = (label, action, title = label) => <button type="button" title={title} aria-label={label} className={button} onClick={() => action()}>{label}</button>;
  const indent = direction => {
    if (editor.isActive("listItem")) { editor.chain().focus()[direction > 0 ? "sinkListItem" : "liftListItem"]("listItem").run(); return; }
    const node = editor.isActive("heading") ? "heading" : "paragraph";
    const current = editor.getAttributes(node).indent || 0;
    editor.chain().focus().updateAttributes(node, { indent: Math.max(0, Math.min(4, current + direction)) }).run();
  };
  return <div className="border border-[#E5E0D8] rounded-lg overflow-hidden bg-white">
    <div role="toolbar" aria-label="Outline formatting" className="flex flex-wrap items-center gap-1 p-2 border-b bg-[#FAF9F6]">
      {control("Bold", () => editor.chain().focus().toggleBold().run())}
      {control("Italic", () => editor.chain().focus().toggleItalic().run())}
      {control("Underline", () => editor.chain().focus().toggleUnderline().run())}
      {control("Bullets", () => editor.chain().focus().toggleBulletList().run())}
      {control("Numbers", () => editor.chain().focus().toggleOrderedList().run())}
      <select aria-label="Heading" className={button} value={editor.isActive("heading") ? editor.getAttributes("heading").level : 0}
        onChange={e => Number(e.target.value) ? editor.chain().focus().toggleHeading({ level: Number(e.target.value) }).run() : editor.chain().focus().setParagraph().run()}>
        <option value={0}>Paragraph</option><option value={2}>Heading 2</option><option value={3}>Heading 3</option>
      </select>
      <select aria-label="Font size" className={button} defaultValue="" onChange={e => editor.chain().focus().setFontSize(e.target.value).run()}>
        <option value="" disabled>Size</option>{[12, 14, 16, 18, 24, 32].map(size => <option key={size} value={`${size}px`}>{size}px</option>)}
      </select>
      {control("Left", () => editor.chain().focus().setTextAlign("left").run())}
      {control("Center", () => editor.chain().focus().setTextAlign("center").run())}
      {control("Right", () => editor.chain().focus().setTextAlign("right").run())}
      {control("Indent +", () => indent(1))}{control("Indent −", () => indent(-1))}
      <label className="text-xs flex items-center gap-1">Text color<input type="color" aria-label="Text color" defaultValue="#1c201e" onChange={e => editor.chain().focus().setColor(e.target.value).run()} /></label>
      <label className="text-xs flex items-center gap-1">Highlight<input type="color" aria-label="Highlight color" defaultValue="#fff2a8" onChange={e => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} /></label>
      {control("Link", () => { const url = window.prompt("Link URL (https://)"); if (url?.startsWith("https://")) editor.chain().focus().setLink({ href: url }).run(); })}
      {control("Undo", () => editor.chain().focus().undo().run())}{control("Redo", () => editor.chain().focus().redo().run())}
    </div>
    <EditorContent editor={editor} />
  </div>;
}
